// UPPDRAG-20: rewritten to use the shared canonical hook
// (useUnpaidPayments). The previous inline queries here were silently
// broken in prod — `profiles:user_id(...)` returned HTTP 400 because
// the FK was missing (added in migration 20260428120000) and React
// Query swallowed the error, rendering "Alla betalningar hanterade!"
// regardless of actual data. The dashboard count card and this list
// page now share filter logic via useUnpaidPayments + useUnpaidCount.

import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useUnpaidPayments, type UnpaidItem } from "@/hooks/useUnpaidPayments";
import { formatPrice } from "@/utils/formatPrice";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import { ArrowLeft, Loader2, Check, X, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

type TypeFilter = "all" | "booking" | "membership";
type SortKey = "date" | "amount" | "urgency";

export default function DashboardUnpaid() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hallId } = useOrgHall();
  const qc = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  // G-103: cancelling an unpaid booking is destructive — confirm first.
  const [confirmCancelBookingId, setConfirmCancelBookingId] = useState<string | null>(null);

  const { items: unpaidItems, isLoading, refetchAll } = useUnpaidPayments(hallId);

  const allItems = useMemo(() => {
    let items = [...unpaidItems];
    if (typeFilter !== "all") {
      items = items.filter((i) => i.type === typeFilter);
    }
    items.sort((a, b) => {
      if (sortKey === "amount") return b.amountMinor - a.amountMinor;
      if (sortKey === "urgency")
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return items;
  }, [unpaidItems, typeFilter, sortKey]);

  const totalOutstandingMinor = allItems.reduce((sum, i) => sum + i.amountMinor, 0);

  // Mark as paid mutation
  const markPaid = useMutation({
    mutationFn: async (item: UnpaidItem) => {
      if (item.type === "booking") {
        const { error } = await supabase
          .from("booking_payments")
          .update({
            status: "paid",
            paid_at: new Date().toISOString(),
            payment_method: "manual",
            marked_paid_by: user?.id ?? null,
          })
          .eq("id", item.paymentId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("memberships")
          .update({ status: "active" })
          .eq("id", item.membershipId!);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(t("dashboard.markedAsPaid", "Markerad som betald"));
      // Invalidate both the count + items queries (shared keys live in
      // useUnpaidPayments — keep these in sync if those keys change).
      qc.invalidateQueries({ queryKey: ["unpaid-bookings"] });
      qc.invalidateQueries({ queryKey: ["unpaid-memberships"] });
      qc.invalidateQueries({ queryKey: ["unpaid-count-bookings"] });
      qc.invalidateQueries({ queryKey: ["unpaid-count-memberships"] });
      refetchAll();
    },
    onError: () => {
      toast.error(t("common.error", "Något gick fel"));
    },
  });

  // Cancel booking mutation
  const cancelBooking = useMutation({
    mutationFn: async (bookingId: string) => {
      const { error } = await supabase
        .from("bookings")
        .update({ status: "cancelled" })
        .eq("id", bookingId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("dashboard.bookingCancelled", "Bokning avbokad"));
      qc.invalidateQueries({ queryKey: ["unpaid-bookings"] });
      qc.invalidateQueries({ queryKey: ["unpaid-count-bookings"] });
      refetchAll();
    },
    onError: () => {
      toast.error(t("common.error", "Något gick fel"));
    },
  });

  const typeFilters: { key: TypeFilter; label: string }[] = [
    { key: "all", label: t("common.all", "Alla") },
    { key: "booking", label: t("dashboard.bookings", "Bokningar") },
    { key: "membership", label: t("dashboard.memberships", "Medlemskap") },
  ];

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: "date", label: t("dashboard.newest", "Senaste") },
    { key: "urgency", label: t("dashboard.oldest", "Äldsta") },
    { key: "amount", label: t("dashboard.highestAmount", "Högst belopp") },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/manager/dashboard"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold">
          {t("dashboard.unpaidItems", "Obetalda")} ({allItems.length})
        </h1>
      </div>

      {/* Filters and sort */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap">
          {typeFilters.map((f) => (
            <Button
              key={f.key}
              variant={typeFilter === f.key ? "default" : "outline"}
              size="sm"
              onClick={() => setTypeFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          {sortOptions.map((s) => (
            <Button
              key={s.key}
              variant={sortKey === s.key ? "default" : "outline"}
              size="sm"
              onClick={() => setSortKey(s.key)}
            >
              {s.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Items list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : allItems.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Check className="h-8 w-8 mx-auto mb-2 text-green-500" />
            {t("dashboard.allPaid", "Alla betalningar hanterade!")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {allItems.map((item) => {
            const dueLabel = formatDistanceToNow(new Date(item.createdAt), {
              locale: sv,
              addSuffix: true,
            });
            return (
              <Card key={`${item.type}-${item.id}`} className="border-l-4 border-l-red-500">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                        <span className="font-medium truncate">{item.userName}</span>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {item.type === "booking"
                            ? t("dashboard.booking", "Bokning")
                            : t("dashboard.membership", "Medlemskap")}
                        </Badge>
                        {item.paymentStatus === "failed" && (
                          <Badge variant="destructive" className="text-xs shrink-0">
                            {t("dashboard.failed", "Misslyckad")}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("dashboard.created", "Skapad")} {dueLabel}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-lg font-bold">{formatPrice({ valueMinor: item.amountMinor, currency: item.currency })}</span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => markPaid.mutate(item)}
                      disabled={markPaid.isPending}
                    >
                      <Check className="h-3.5 w-3.5 mr-1" />
                      {t("dashboard.markAsPaid", "Markera betald")}
                    </Button>
                    {item.type === "booking" && item.bookingId && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmCancelBookingId(item.bookingId!)}
                        disabled={cancelBooking.isPending}
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        {t("dashboard.cancelBooking", "Avboka")}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* G-103: confirm before cancelling an unpaid booking */}
      <AlertDialog open={!!confirmCancelBookingId} onOpenChange={(open) => { if (!open) setConfirmCancelBookingId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dashboard.confirmCancelTitle", "Avboka bokningen?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("dashboard.confirmCancelDescription", "Bokningen avbokas och tiden släpps. Denna åtgärd kan inte ångras.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Avbryt")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirmCancelBookingId) cancelBooking.mutate(confirmCancelBookingId);
                setConfirmCancelBookingId(null);
              }}
            >
              {t("dashboard.cancelBooking", "Avboka")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Total outstanding + bulk action */}
      {allItems.length > 0 && (
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <span className="text-sm text-muted-foreground">
                {t("dashboard.totalOutstanding", "Totalt utestående")}:
              </span>{" "}
              <span className="text-xl font-bold">{formatPrice({ valueMinor: totalOutstandingMinor, currency: "SEK" })}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
